import { useMemo, useCallback } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getRouteApi } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
// api
import { accountActivationBackendApi } from "src/backendApi/backendApi";
// types
import type {
  AccountActivationQueryParam,
  AccountCreateLambdaResponse,
} from "./accountActivationTypes";

/**
 * PARSE URL PARAM
 * ?o=abc123 (b64 encoded JSON string containing shopify order details which are used to look up account)
 */

const routeApi = getRouteApi("/start");

type ParsedAcctActivateQueryParams =
  | {
      isError: false;
      value: AccountActivationQueryParam;
    }
  | {
      isError: true;
      value: undefined;
    };

export const useParsedAcctActivationQueryParam =
  (): ParsedAcctActivateQueryParams => {
    const search = routeApi.useSearch();

    return useMemo<ParsedAcctActivateQueryParams>(() => {
      try {
        const uriDecodedUrlParam = decodeURIComponent(search.o);
        const decoded = atob(uriDecodedUrlParam);
        return {
          isError: false,
          value: JSON.parse(decoded),
        };
      } catch (e) {
        console.error(e);
        Sentry.captureException(e);
        return {
          isError: true,
          value: undefined,
        };
      }
    }, [search.o]);
  };

/**
 * CREATE ACCOUNT - runs on page load
 */

export const useCreateAccountQuery = (): UseQueryResult<
  AccountCreateLambdaResponse | undefined
> => {
  const search = routeApi.useSearch();

  const { value: parsedUrlParam } = useParsedAcctActivationQueryParam();

  const createAccount = useCallback(async () => {
    if (parsedUrlParam === undefined) {
      return;
    }
    const resp =
      await accountActivationBackendApi.shopifyOrderGetOrCreate(parsedUrlParam);

    return resp.data;
  }, [parsedUrlParam]);

  return useQuery<AccountCreateLambdaResponse | undefined>({
    queryKey: ["CREATE_ACCOUNT"],
    queryFn: createAccount,
    enabled: search.o !== undefined,
    // make sure it only fires one time (unless unsuccessful)
    staleTime: Infinity,
    gcTime: Infinity,
  });
};
